import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Block, closeModal, DefaultText, FlexRow } from '@guryou/html-components';
import { useOnboardContext } from 'onboard/context';
import { dealTokens, SystemButton, SystemCheckbox } from 'components/shared';

import OnboardingDateRange from './OnboardingDateRange';

// A day's default hours, 09:00–23:00. Full date-times, not bare times: the hour picker parses its
// value with parseISO, which gives Invalid Date for '09:00:00' (so a freshly ticked day showed
// empty fields), and the API reads the saved value with printInputTime(...) || '09:00'/'17:00', so
// an untouched bare time was saved as 09:00–17:00. Only the time part is used; Monday's default
// has always had this shape, and every day now shares it.
const DEFAULT_START_TIME = '2018-01-01T09:00:00';
const DEFAULT_END_TIME = '2038-01-01T23:00:00';

const defaultWorkTime = { startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME };

const localSchedules = [
  {
    day: 1,
    workTime: defaultWorkTime,
    break: null,
  },
  {
    day: 2,
    workTime: null,
    break: null,
  },
  {
    day: 3,
    workTime: null,
    break: null,
  },
  {
    day: 4,
    workTime: null,
    break: null,
  },
  {
    day: 5,
    workTime: null,
    break: null,
  },
  {
    day: 6,
    workTime: null,
    break: null,
  },
  {
    day: 7,
    workTime: null,
    break: null,
  },
];

const OnboardWorkingHours = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { schedules, setSchedules } = useOnboardContext();
  const [localState, setLocalState] = useState(schedules || localSchedules);
  // The legacy CheckBox kept its own ticked state after mount; SystemCheckbox is controlled, so
  // "same hours all week" needs real state here.
  const [sameTimeAllWeek, setSameTimeAllWeek] = useState(false);

  const handleSetFullWeek = checked => {
    setSameTimeAllWeek(checked);
    const firstWorkTime = localState[0]?.workTime;
    if (firstWorkTime && checked) {
      const fullSchedule = [1, 2, 3, 4, 5, 6, 7].map(x => ({ day: x, workTime: firstWorkTime, break: null }));

      setLocalState(fullSchedule);
    }
    if (!checked) {
      const fullSchedule = [1, 2, 3, 4, 5, 6, 7].map(x => ({ day: x, workTime: x === 1 ? firstWorkTime : null, break: null }));
      setLocalState(fullSchedule);
    }
  };

  const handleClose = () => {
    dispatch(closeModal('setWorkingHours'));
  };

  const handleSave = () => {
    if (localState?.some(x => x.workTime !== null)) {
      setSchedules(localState);
    }
    dispatch(closeModal('setWorkingHours'));
  };

  return (
    <>
      <FlexRow margin={0} height="60vh" overflowY="auto">
        <Block xs={12}>
          <FlexRow xs={12}>
            <Block xs={4}>
              <DefaultText fontWeight={800}>{t('GENERAL.day')}</DefaultText>
            </Block>
            <Block xs={8} marginLeft={5}>
              <DefaultText fontWeight={800}>{t('GENERAL.workingHours')}</DefaultText>
            </Block>
          </FlexRow>
          {localState.map((x, i) => (
            <React.Fragment key={`day_${x.day}`}>
              <FlexRow alignItems="end" margin={0} marginBottom={`${dealTokens.space[3]}px`}>
                <Block lg={4} md={12} xs={12} margin={2}>
                  {/* Controlled by the day's own workTime — no `key={x.workTime}` remount needed to keep
                      it in sync, as the legacy CheckBox did. The modal saves from localState, not a
                      Form, so the old name/value attributes were never submitted and are dropped. */}
                  <SystemCheckbox
                    checked={!!x.workTime}
                    label={t(`GENERAL.days_${x.day}`)}
                    onChange={checked =>
                      setLocalState(prev =>
                        prev.map((d, idx) => (idx === x.day - 1 ? { day: x.day, workTime: checked ? defaultWorkTime : null } : d))
                      )
                    }
                  />
                </Block>

                {!!x.workTime && (
                  <>
                    <OnboardingDateRange
                      inline={true}
                      margin={'wizard'}
                      wrapperXs={12}
                      wrapperMd={12}
                      wrapperLg={8}
                      wrapperMargin={0}
                      startTimeFieldName={`schedules[${x.day - 1}].workTime.startTime`}
                      endTimeFieldName={`schedules[${x.day - 1}].workTime.endTime`}
                      startValue={x.workTime.startTime}
                      endValue={x.workTime.endTime}
                      onChange={(startTime, endTime) =>
                        setLocalState(prevState => {
                          prevState[i].workTime = { startTime, endTime };
                          return [...prevState];
                        })
                      }
                      // These are plain local wall-clock strings ('09:00:00'), not
                      // floating-UTC ones — z=true (the DateTimePicker default) force-appends a
                      // 'Z' before parsing, which reads "09:00" as "09:00 UTC" and displays it
                      // shifted by the browser's local offset (an extra hour in winter/CET,
                      // two in summer/CEST — the exact bug this fixes). z={false} tells it to
                      // parse the string as already-local, no shift.
                      z={false}
                      key={`work-${i}`}
                    />
                  </>
                )}
              </FlexRow>
              {localState[0].workTime && i === 0 && (
                <Block
                  xs={12}
                  display="flex"
                  flexDirection="row"
                  justifyContent="center"
                  marginTop={`${dealTokens.space[1]}px`}
                  marginBottom={`${dealTokens.space[4]}px`}
                >
                  <Block
                    lg={9}
                    md={12}
                    xs={12}
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                    border={`1px solid ${dealTokens.color.border}`}
                    borderRadius={dealTokens.radius.sm}
                  >
                    <div style={{ padding: `${dealTokens.space[2]}px ${dealTokens.space[3]}px`, width: '100%' }}>
                      <SystemCheckbox checked={sameTimeAllWeek} label={t('ONBOARD.setSameTime')} onChange={handleSetFullWeek} />
                    </div>
                  </Block>
                </Block>
              )}
            </React.Fragment>
          ))}
        </Block>
      </FlexRow>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: dealTokens.space[3] }}>
        <SystemButton variant="ghost" onClick={handleClose}>
          {t('ONBOARD.cancel')}
        </SystemButton>
        <SystemButton variant="primary" onClick={handleSave}>
          {t('ONBOARD.save')}
        </SystemButton>
      </div>
    </>
  );
};

export default OnboardWorkingHours;
